import React, { useState, useEffect, useCallback } from 'react';
import { inventoryApi, depotsApi, lookupsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SlideDrawer } from '../components/ui/slide-drawer';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, AlertTriangle, Loader2, Package, ShoppingCart, BarChart3, Truck, Pencil, Settings2 } from 'lucide-react';

const money = (n) => `A$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS = {
  in_stock: { label: 'In stock', cls: 'bg-[#EAF6EC] text-[#2F7D3A] border-[#BFE3C6]' },
  low: { label: 'Low', cls: 'bg-[#FEF6E0] text-[#92600A] border-[#EBD48F]' },
  out_of_stock: { label: 'Out of stock', cls: 'bg-[#FDECEA] text-[#B91C1C] border-[#F3C4BD]' },
};
const StatusPill = ({ s }) => {
  const m = STATUS[s] || STATUS.in_stock;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-[5px] border text-[10px] font-bold uppercase tracking-wide ${m.cls}`}>{m.label}</span>;
};
const TH = ({ children, right }) => (
  <th className={`px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A857A] ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);

export default function InventoryPage() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('parts');
  const [parts, setParts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [value, setValue] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [formPart, setFormPart] = useState(null); // 'new' | partObject | null
  const [poSeed, setPoSeed] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d, a, v] = await Promise.all([
        inventoryApi.listParts(search ? { search } : {}),
        depotsApi.list(),
        inventoryApi.alerts(),
        inventoryApi.reportValue(),
      ]);
      setParts(p.data || []);
      setDepots(d.data || []);
      setAlerts(a.data || []);
      setValue(v.data || null);
    } catch (e) { toast.error('Failed to load inventory'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const lowCount = parts.filter((p) => p.status === 'low').length;
  const outCount = parts.filter((p) => p.status === 'out_of_stock').length;
  const bands = [
    { key: 'all', label: 'All parts', count: parts.length, num: 'text-foreground', dot: '#C9A400' },
    { key: 'low', label: 'Low stock', count: lowCount, num: 'text-[#92600A]', dot: '#D9A400' },
    { key: 'out_of_stock', label: 'Out of stock', count: outCount, num: 'text-[#B91C1C]', dot: '#DC2626' },
    { key: 'value', label: 'Inventory value', count: money(value?.total_value), num: 'text-foreground', dot: '#3E9B4F', wide: true },
  ];
  const categoryOptions = [...new Set(parts.map((p) => p.category).filter(Boolean))].sort();
  const supplierOptions = [...new Set(parts.map((p) => p.supplier).filter(Boolean))].sort();
  const visible = parts.filter((p) =>
    (statusFilter === 'all' || statusFilter === 'value' || p.status === statusFilter)
    && (categoryFilter === 'all' || p.category === categoryFilter)
    && (supplierFilter === 'all' || p.supplier === supplierFilter));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Inventory</h1>
          <p className="text-muted-foreground">Parts catalogue, per-depot stock and purchasing across your fleet</p>
        </div>
        <Button onClick={() => setFormPart('new')} className="btn-industrial" data-testid="inv-add-part-btn">
          <Plus className="w-5 h-5 mr-2" />Add Part
        </Button>
      </div>

      {/* Band cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {bands.map((band) => {
          const selectable = band.key !== 'value';
          const sel = statusFilter === band.key && selectable;
          return (
            <button key={band.key} type="button" disabled={!selectable}
              onClick={() => selectable && setStatusFilter(sel ? 'all' : band.key)}
              data-testid={`inv-band-${band.key}`}
              className={`text-left rounded-xl border p-4 transition-colors ${sel ? 'border-[#C9A400] ring-2 ring-offset-1 ring-[#C9A400] bg-white' : 'border-[#E6E2D9] bg-white hover:border-[#D4CFC2]'} ${selectable ? '' : 'cursor-default'}`}>
              <div className="flex items-start justify-between">
                <div className={`${band.wide ? 'text-[20px]' : 'text-[28px]'} leading-tight font-extrabold ${band.num}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{band.count}</div>
                <span className="w-2.5 h-2.5 rounded-full mt-2" style={{ background: band.dot }} />
              </div>
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A857A] mt-1">{band.label}</div>
            </button>
          );
        })}
      </div>

      {/* Low-stock alerts */}
      {alerts.length > 0 && (
        <Card className="border-[#EBD48F] bg-[#FEFBF0] overflow-hidden" data-testid="inv-alerts">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-[#92600A]" /><span className="text-sm font-bold uppercase tracking-wide text-[#92600A]">Low stock</span></div>
            <div className="flex flex-col divide-y divide-[#EFE4C0]">
              {alerts.slice(0, 6).map((a) => (
                <div key={a.part_id + a.depot_id} className="flex items-center justify-between py-1.5 text-sm" data-testid="inv-alert-row">
                  <span className="text-foreground"><b>{a.name}</b> <span className="text-muted-foreground">at {a.depot_name} — {a.on_hand} on hand (reorder at {a.reorder_point})</span></span>
                  <Button size="sm" variant="outline" onClick={() => { setPoSeed(a); setTab('purchase'); }} data-testid="inv-alert-reorder">Reorder</Button>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Main card */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
          <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1">
            {[['parts', 'Parts', Package], ['purchase', 'Purchase Orders', ShoppingCart], ['reports', 'Reports', BarChart3]].map(([id, label, Icon]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`inv-tab-${id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${tab === id ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                <Icon className="w-4 h-4" />{label}
              </button>
            ))}
          </div>
          {tab === 'parts' && (
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="h-10 rounded-md border border-border bg-background px-2 text-sm" data-testid="inv-filter-category">
                <option value="all">All categories</option>
                {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)} className="h-10 rounded-md border border-border bg-background px-2 text-sm" data-testid="inv-filter-supplier">
                <option value="all">All suppliers</option>
                {supplierOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Part number, name or scan code…" className="pl-10 pr-14" data-testid="inv-search" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-muted-foreground">{visible.length}/{parts.length}</span>
              </div>
            </div>
          )}
        </div>

        {tab === 'parts' && (
          loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C9A400]" /></div> :
            visible.length === 0 ? <p className="py-16 text-center text-muted-foreground">No parts to show.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-border"><TH>Part</TH><TH>Category</TH><TH right>Total on hand</TH><TH right>Unit cost</TH><TH>Status</TH><TH right></TH></tr></thead>
                  <tbody>
                    {visible.map((p) => (
                      <tr key={p.id} className="border-b border-border hover:bg-secondary/40 cursor-pointer" onClick={() => setSelected(p)} data-testid={`inv-part-row-${p.id}`}>
                        <td className="px-4 py-2.5"><div className="font-semibold text-foreground">{p.name}</div><div className="text-xs font-mono text-muted-foreground">{p.part_number}{p.active === false ? ' · inactive' : ''}</div></td>
                        <td className="px-4 py-2.5 text-sm text-muted-foreground">{p.category || '—'}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{p.total_on_hand} {p.unit || ''}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{money(p.unit_cost)}</td>
                        <td className="px-4 py-2.5"><StatusPill s={p.status} /></td>
                        <td className="px-4 py-2.5 text-right"><Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelected(p); }}>Open</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        )}
        {tab === 'purchase' && <PurchaseOrders depots={depots} parts={parts} seed={poSeed} clearSeed={() => setPoSeed(null)} onChanged={load} />}
        {tab === 'reports' && <Reports value={value} />}
      </Card>

      {selected && <PartDrawer partId={selected.id} depots={depots} isAdmin={isAdmin()} onClose={() => setSelected(null)} onEdit={(p) => { setSelected(null); setFormPart(p); }} onChanged={load} />}
      {formPart && <PartFormDrawer part={formPart === 'new' ? null : formPart} onClose={() => setFormPart(null)} onSaved={() => { setFormPart(null); load(); }} />}
    </div>
  );
}

const LOOKUP_SELECT_CLS = "h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A400]/40";
const FieldLabel = ({ children, required }) => (
  <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A857A]">{children}{required && <span className="text-[#B91C1C]"> *</span>}</label>
);

// Native select that always includes the part's current (possibly legacy free-text) value.
const LookupSelect = ({ value, onChange, options, placeholder, testId, useName }) => {
  const key = (o) => (useName ? o.name : o.value);
  const known = options.some((o) => key(o) === value);
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={LOOKUP_SELECT_CLS} data-testid={testId}>
      <option value="">{placeholder}</option>
      {!known && value ? <option value={value}>{value} (legacy)</option> : null}
      {options.map((o) => <option key={o.id} value={key(o)}>{o.name}</option>)}
    </select>
  );
};

const PartFormDrawer = ({ part, onClose, onSaved }) => {
  const editing = !!part;
  const [f, setF] = useState({
    part_number: part?.part_number || '', name: part?.name || '', description: part?.description || '',
    category: part?.category || '', unit: part?.unit || 'each', unit_cost: part?.unit_cost ?? '',
    supplier: part?.supplier || '', barcode: part?.barcode || '',
  });
  const [opts, setOpts] = useState({ category: [], unit: [], supplier: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, u, s] = await Promise.all([
          lookupsApi.getByCategory('part_category'),
          lookupsApi.getByCategory('part_unit'),
          lookupsApi.getByCategory('part_supplier'),
        ]);
        setOpts({
          category: (c.data || []).filter((x) => x.is_active !== false),
          unit: (u.data || []).filter((x) => x.is_active !== false),
          supplier: (s.data || []).filter((x) => x.is_active !== false),
        });
      } catch { /* dropdowns simply stay empty */ }
    })();
  }, []);

  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!f.part_number.trim() || !f.name.trim()) { toast.error('Part number and name are required'); return; }
    setBusy(true);
    try {
      const payload = { ...f, unit_cost: Number(f.unit_cost || 0) };
      if (editing) { await inventoryApi.updatePart(part.id, payload); toast.success('Part updated'); }
      else { await inventoryApi.createPart(payload); toast.success('Part added'); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save part'); }
    finally { setBusy(false); }
  };

  return (
    <SlideDrawer open size="lg" onOpenChange={(o) => { if (!o) onClose(); }} dismissable
      title={editing ? `Edit ${part.part_number}` : 'Add part'}
      description={editing ? 'Update the catalogue details for this part.' : 'Add a new part to the catalogue. Set stock levels per depot afterwards.'}>
      <div className="flex flex-col gap-5 p-1" data-testid="inv-part-form">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <FieldLabel required>Part number</FieldLabel>
            <Input value={f.part_number} onChange={(e) => set('part_number')(e.target.value)} placeholder="e.g. FLT-1032" data-testid="inv-f-number" />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel>Unit cost (A$)</FieldLabel>
            <Input type="number" step="0.01" value={f.unit_cost} onChange={(e) => set('unit_cost')(e.target.value)} placeholder="0.00" data-testid="inv-f-cost" />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel required>Name</FieldLabel>
          <Input value={f.name} onChange={(e) => set('name')(e.target.value)} placeholder="e.g. Hydraulic oil filter" data-testid="inv-f-name" />
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel>Description</FieldLabel>
          <Input value={f.description} onChange={(e) => set('description')(e.target.value)} placeholder="Optional notes" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <FieldLabel>Category</FieldLabel>
            <LookupSelect value={f.category} onChange={set('category')} options={opts.category} useName placeholder="Select category…" testId="inv-f-category" />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel>Unit</FieldLabel>
            <LookupSelect value={f.unit} onChange={set('unit')} options={opts.unit} placeholder="Select unit…" testId="inv-f-unit" />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel>Supplier</FieldLabel>
          <LookupSelect value={f.supplier} onChange={set('supplier')} options={opts.supplier} useName placeholder="Select supplier…" testId="inv-f-supplier" />
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel>Barcode / QR value</FieldLabel>
          <Input value={f.barcode} onChange={(e) => set('barcode')(e.target.value)} placeholder="Scan or type a code" data-testid="inv-f-barcode" />
        </div>

        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Settings2 className="w-3.5 h-3.5" />
          Manage categories, units and suppliers in <a href="/data-management" className="text-[#92600A] font-semibold hover:underline">Data Management</a>.
        </p>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="ghost" onClick={onClose} data-testid="inv-f-cancel">Cancel</Button>
          <Button disabled={busy} onClick={save} className="btn-industrial" data-testid="inv-f-save">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing ? 'Save changes' : 'Add part'}
          </Button>
        </div>
      </div>
    </SlideDrawer>
  );
};

const PartDrawer = ({ partId, depots, isAdmin, onClose, onEdit, onChanged }) => {
  const [part, setPart] = useState(null);
  const [adj, setAdj] = useState({ depot_id: '', new_qty: '', reorder_point: '', reorder_qty: '' });
  const reload = useCallback(async () => {
    const r = await inventoryApi.getPart(partId); setPart(r.data);
    if (!adj.depot_id && depots[0]) setAdj((a) => ({ ...a, depot_id: depots[0].id }));
  }, [partId, depots]); // eslint-disable-line
  useEffect(() => { reload(); }, [reload]);
  const depotName = (id) => depots.find((d) => d.id === id)?.name || id;
  const stockFor = (id) => (part?.stock || []).find((s) => s.depot_id === id);

  const applyLevels = async () => {
    if (!adj.depot_id) return;
    try {
      await inventoryApi.setStock(partId, adj.depot_id, {
        on_hand: adj.new_qty === '' ? undefined : Number(adj.new_qty),
        reorder_point: adj.reorder_point === '' ? undefined : Number(adj.reorder_point),
        reorder_qty: adj.reorder_qty === '' ? undefined : Number(adj.reorder_qty),
      });
      toast.success('Stock level saved'); setAdj((a) => ({ ...a, new_qty: '' })); reload(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!part) return null;
  return (
    <SlideDrawer open size="lg" onClose={onClose} title={`Part ${part.part_number}`}>
      <div className="flex flex-col gap-4 p-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>{part.name}</div>
            <div className="text-sm text-muted-foreground">{part.description || '—'}</div>
            <div className="text-xs text-muted-foreground mt-1 font-mono">{money(part.unit_cost)} · {part.category || 'no category'} · {part.unit || 'each'} · {part.supplier || 'no supplier'} · barcode {part.barcode || '—'}</div>
          </div>
          <Button size="sm" variant="outline" onClick={() => onEdit && onEdit(part)} data-testid="inv-part-edit-btn"><Pencil className="w-4 h-4 mr-1.5" />Edit details</Button>
        </div>

        <Card className="p-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A857A] mb-2">Stock by depot</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground"><th className="py-1">Depot</th><th className="text-right">On hand</th><th className="text-right">Reorder at</th><th>Status</th></tr></thead>
            <tbody>
              {depots.map((d) => { const s = stockFor(d.id); return (
                <tr key={d.id} className="border-t border-border">
                  <td className="py-1.5">{d.name}</td>
                  <td className="py-1.5 text-right font-mono">{s?.on_hand ?? 0}</td>
                  <td className="py-1.5 text-right font-mono">{s?.reorder_point ?? 0}</td>
                  <td className="py-1.5"><StatusPill s={s?.status || (s?.on_hand > 0 ? 'in_stock' : 'out_of_stock')} /></td>
                </tr>); })}
            </tbody>
          </table>
        </Card>

        <Card className="p-4" data-testid="inv-adjust">
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A857A] mb-2">Set levels / adjust</div>
          <div className="flex flex-wrap gap-2 items-end">
            <select value={adj.depot_id} onChange={(e) => setAdj({ ...adj, depot_id: e.target.value })} className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="inv-adj-depot">
              {depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <Input type="number" placeholder="On hand" value={adj.new_qty} onChange={(e) => setAdj({ ...adj, new_qty: e.target.value })} className="w-28" data-testid="inv-adj-qty" />
            <Input type="number" placeholder="Reorder point" value={adj.reorder_point} onChange={(e) => setAdj({ ...adj, reorder_point: e.target.value })} className="w-32" data-testid="inv-adj-rop" />
            <Input type="number" placeholder="Reorder qty" value={adj.reorder_qty} onChange={(e) => setAdj({ ...adj, reorder_qty: e.target.value })} className="w-28" />
            <Button size="sm" onClick={applyLevels} className="btn-industrial" data-testid="inv-adj-save">Save</Button>
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A857A] mb-2">Movement history</div>
          {(part.movements || []).length === 0 ? <p className="text-sm text-muted-foreground">No movements yet.</p> : (
            <div className="flex flex-col divide-y divide-border">
              {part.movements.map((m) => (
                <div key={m.id} className="flex items-center justify-between py-1.5 text-sm">
                  <span><span className={`font-mono font-bold ${m.delta < 0 ? 'text-[#B91C1C]' : 'text-[#2F7D3A]'}`}>{m.delta > 0 ? '+' : ''}{m.delta}</span> <span className="text-muted-foreground">· {m.cause.replace(/_/g, ' ')}{m.ref_label ? ` (${m.ref_label})` : ''} · {depotName(m.depot_id)}</span></span>
                  <span className="text-xs text-muted-foreground font-mono">{(m.created_at || '').slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </SlideDrawer>
  );
};

const PurchaseOrders = ({ depots, parts, seed, clearSeed, onChanged }) => {
  const [pos, setPos] = useState([]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => { const r = await inventoryApi.listPurchaseOrders(); setPos(r.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (seed) setCreating(true); }, [seed]);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A857A]">Purchase orders</span>
        <Button size="sm" onClick={() => setCreating(true)} className="btn-industrial" data-testid="inv-po-new"><Plus className="w-4 h-4 mr-1" />New PO</Button>
      </div>
      {pos.length === 0 ? <p className="py-10 text-center text-muted-foreground">No purchase orders yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border"><TH>Reference</TH><TH>Supplier</TH><TH>Depot</TH><TH right>Lines</TH><TH>Status</TH><TH right></TH></tr></thead>
            <tbody>
              {pos.map((p) => (
                <tr key={p.id} className="border-b border-border hover:bg-secondary/40" data-testid={`inv-po-row-${p.id}`}>
                  <td className="px-4 py-2 font-mono">{p.reference}</td><td className="px-4 py-2">{p.supplier}</td><td className="px-4 py-2">{p.depot_name}</td>
                  <td className="px-4 py-2 text-right">{p.lines?.length || 0}</td>
                  <td className="px-4 py-2"><span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-[5px] border border-border bg-secondary">{p.status}</span></td>
                  <td className="px-4 py-2 text-right"><Button size="sm" variant="outline" onClick={() => setOpen(p)}>Open</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <CreatePODialog depots={depots} parts={parts} seed={seed} onClose={() => { setCreating(false); clearSeed(); }} onSaved={() => { setCreating(false); clearSeed(); load(); }} />}
      {open && <PODrawer po={open} onClose={() => setOpen(null)} onChanged={() => { load(); onChanged(); }} />}
    </div>
  );
};

const CreatePODialog = ({ depots, parts, seed, onClose, onSaved }) => {
  const [supplier, setSupplier] = useState(seed?.supplier || '');
  const [supplierOpts, setSupplierOpts] = useState([]);
  const [depotId, setDepotId] = useState(seed?.depot_id || depots[0]?.id || '');
  const [lines, setLines] = useState(seed ? [{ part_id: seed.part_id, quantity: String(seed.reorder_qty || 1), unit_cost: '' }] : [{ part_id: '', quantity: '1', unit_cost: '' }]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => {
    try { const r = await lookupsApi.getByCategory('part_supplier'); setSupplierOpts((r.data || []).filter((x) => x.is_active !== false)); } catch { /* empty */ }
  })(); }, []);
  const save = async () => {
    const valid = lines.filter((l) => l.part_id && Number(l.quantity) > 0);
    if (!supplier.trim() || !depotId || valid.length === 0) { toast.error('Supplier, depot and at least one line required'); return; }
    setBusy(true);
    try {
      await inventoryApi.createPurchaseOrder({ supplier: supplier.trim(), depot_id: depotId, lines: valid.map((l) => ({ part_id: l.part_id, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost || 0) })) });
      toast.success('Purchase order created'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-2">
          <LookupSelect value={supplier} onChange={setSupplier} options={supplierOpts} useName placeholder="Select supplier…" testId="inv-po-supplier" />
          <select value={depotId} onChange={(e) => setDepotId(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="inv-po-depot">
            {depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          {lines.map((l, i) => (
            <div key={i} className="flex gap-2">
              <select value={l.part_id} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, part_id: e.target.value } : x))} className="h-9 rounded-md border border-border bg-background px-2 text-sm flex-1" data-testid={`inv-po-part-${i}`}>
                <option value="">Select part…</option>
                {parts.map((p) => <option key={p.id} value={p.id}>{p.part_number} — {p.name}</option>)}
              </select>
              <Input type="number" placeholder="Qty" value={l.quantity} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} className="w-20" />
              <Input type="number" placeholder="Unit cost" value={l.unit_cost} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unit_cost: e.target.value } : x))} className="w-28" />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setLines([...lines, { part_id: '', quantity: '1', unit_cost: '' }])}>Add line</Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={save} className="btn-industrial" data-testid="inv-po-save">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PODrawer = ({ po: initial, onClose, onChanged }) => {
  const [po, setPo] = useState(initial);
  const [recv, setRecv] = useState({});
  const refresh = async () => { const r = await inventoryApi.getPurchaseOrder(po.id); setPo(r.data); };
  const markOrdered = async () => { try { await inventoryApi.orderPurchaseOrder(po.id); toast.success('Marked ordered'); refresh(); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const receive = async () => {
    const lines = Object.entries(recv).filter(([, q]) => Number(q) > 0).map(([line_id, q]) => ({ line_id, quantity: Number(q) }));
    if (lines.length === 0) { toast.error('Enter quantities to receive'); return; }
    try { await inventoryApi.receivePurchaseOrder(po.id, { lines }); toast.success('Stock received'); setRecv({}); refresh(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <SlideDrawer open size="lg" onClose={onClose} title={`Purchase Order ${po.reference}`}>
      <div className="flex flex-col gap-4 p-1">
        <div className="text-sm text-muted-foreground">{po.supplier} · status <b className="uppercase text-foreground">{po.status}</b></div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border"><TH>Part</TH><TH right>Ordered</TH><TH right>Received</TH><TH right>Receive now</TH></tr></thead>
          <tbody>
            {(po.lines || []).map((l) => (
              <tr key={l.id} className="border-b border-border">
                <td className="px-2 py-2">{l.name}<div className="text-xs font-mono text-muted-foreground">{l.part_number}</div></td>
                <td className="px-2 py-2 text-right font-mono">{l.quantity}</td>
                <td className="px-2 py-2 text-right font-mono">{l.received_qty || 0}</td>
                <td className="px-2 py-2 text-right">
                  {po.status !== 'received' && (l.received_qty || 0) < l.quantity ? (
                    <Input type="number" min="0" value={recv[l.id] || ''} onChange={(e) => setRecv({ ...recv, [l.id]: e.target.value })} className="w-20 inline-block" data-testid={`inv-po-recv-${l.id}`} />
                  ) : <span className="text-xs text-muted-foreground">done</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex gap-2">
          {po.status === 'draft' && <Button onClick={markOrdered} className="btn-industrial" data-testid="inv-po-order">Mark Ordered</Button>}
          {po.status !== 'received' && <Button variant="outline" onClick={receive} data-testid="inv-po-receive"><Truck className="w-4 h-4 mr-1" />Receive</Button>}
        </div>
      </div>
    </SlideDrawer>
  );
};

const Reports = ({ value }) => {
  const [usage, setUsage] = useState(null);
  useEffect(() => { (async () => { try { const u = await inventoryApi.reportUsage(90); setUsage(u.data); } catch {} })(); }, []);
  return (
    <div className="p-4 grid md:grid-cols-2 gap-4">
      <Card className="p-4" data-testid="inv-report-value">
        <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A857A] mb-1">Inventory value</div>
        <div className="text-2xl font-extrabold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{money(value?.total_value)}</div>
        {(value?.by_depot || []).map((d) => (
          <div key={d.depot_id} className="flex justify-between text-sm border-t border-border py-1.5"><span>{d.depot_name}</span><span className="font-mono">{money(d.value)}</span></div>
        ))}
      </Card>
      <Card className="p-4" data-testid="inv-report-usage">
        <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A857A] mb-1">Parts used (last {usage?.days || 90} days)</div>
        <div className="text-2xl font-extrabold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{money(usage?.total_value)}</div>
        {(usage?.parts || []).slice(0, 10).map((p) => (
          <div key={p.part_id} className="flex justify-between text-sm border-t border-border py-1.5"><span>{p.name} <span className="text-xs text-muted-foreground font-mono">×{p.qty}</span></span><span className="font-mono">{money(p.value)}</span></div>
        ))}
        {(usage?.parts || []).length === 0 && <p className="text-sm text-muted-foreground">No parts consumed yet.</p>}
      </Card>
    </div>
  );
};
